import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, Phone } from "lucide-react";
import { SITE } from "@/lib/site";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Patricia" },
  { to: "/services", label: "Work With LAYOR" },
  { to: "/what-you-will-build", label: "What We Build" },
  { to: "/insights", label: "Insights" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-50 w-full ${scrolled ? "backdrop-blur-md bg-[rgba(250,249,246,0.85)]" : "bg-transparent"} border-b border-transparent ${scrolled ? "border-[var(--layor-border)]" : ""}`}
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
          <img src="/brand/layor-icon.png" alt="LAYOR" className="h-9 w-9 object-contain" />
          <span className="hidden sm:flex flex-col leading-none">
            <span className="font-display-black text-lg text-[var(--layor-navy)]">LAYOR</span>
            <span className="text-[9px] uppercase tracking-[0.32em] text-[var(--layor-muted)]">Digital Group</span>
          </span>
        </Link>

        <nav className="hidden xl:flex items-center gap-6">
          {NAV.map(({ to, label }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              className={({ isActive }) =>
                `whitespace-nowrap text-sm font-medium ${isActive ? "text-[var(--layor-navy)]" : "text-[var(--layor-muted)] hover:text-[var(--layor-navy)]"}`
              }
              data-testid={`nav-link-${to.replace("/", "") || "home"}`}
            >
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3 whitespace-nowrap">
          <Link to="/quote" className="btn-ghost inline-flex items-center justify-center rounded-full px-4 py-2 text-sm font-semibold" data-testid="nav-request-quote">
            Request a Quote
          </Link>
          <Link to="/contact" className="btn-primary inline-flex items-center justify-center rounded-full px-4 py-2 text-sm font-semibold" data-testid="nav-book-consultation">
            Book a Consultation
          </Link>
        </div>

        <button
          type="button"
          aria-label="Open menu"
          data-testid="nav-mobile-toggle"
          className="xl:hidden inline-flex items-center justify-center h-10 w-10 rounded-full border border-[var(--layor-border)] text-[var(--layor-navy)]"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      {open && (
        <div className="xl:hidden border-t border-[var(--layor-border)] bg-[var(--layor-ivory)]" data-testid="nav-mobile-panel">
          <div className="px-6 py-4 flex flex-col gap-2">
            {NAV.map(({ to, label }) => (
              <NavLink key={to} to={to} end={to === "/"} className="py-2 text-[var(--layor-navy)] font-medium">
                {label}
              </NavLink>
            ))}
            <div className="flex gap-2 pt-2">
              <Link to="/quote" className="btn-ghost flex-1 text-center rounded-full px-4 py-2 text-sm font-semibold">Request a Quote</Link>
              <Link to="/contact" className="btn-primary flex-1 text-center rounded-full px-4 py-2 text-sm font-semibold">Book a Consultation</Link>
            </div>
            <a href={SITE.phoneHref} className="inline-flex items-center gap-2 text-sm text-[var(--layor-muted)] pt-2">
              <Phone size={14} /> {SITE.phone}
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export const Footer = () => {
  return (
    <footer className="bg-[var(--layor-black)] text-white mt-24" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-6 md:px-10 py-16 grid gap-12 md:grid-cols-12">
        <div className="md:col-span-5">
          <img src="/brand/layor-lockup-dark.png" alt="LAYOR Digital Group" className="h-16 object-contain" />
          <p className="mt-6 text-white/70 max-w-md leading-relaxed">
            <strong className="block text-white font-semibold mb-2">Leadership-Centered Consulting and Digital Solutions.</strong>
            We help leaders clarify priorities, strengthen decision-making, and turn complex challenges into practical strategies, systems, and solutions that move their organizations forward.
          </p>
          <div className="mt-4 text-[10px] uppercase tracking-[0.32em] text-[var(--layor-gold)]">
            Leadership · Strategy · Technology · <span className="text-[var(--layor-pink)]">Impact</span>
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/contact" className="btn-gold inline-flex rounded-full px-4 py-2 text-sm font-semibold" data-testid="footer-book">Book a Consultation</Link>
            <Link to="/quote" className="inline-flex rounded-full px-4 py-2 text-sm font-semibold border border-white/30 hover:bg-white hover:text-[var(--layor-navy)]" data-testid="footer-quote">Request a Quote</Link>
          </div>
        </div>

        <div className="md:col-span-3">
          <div className="eyebrow font-semibold" style={{ color: "#E6C871" }}>Explore</div>
          <ul className="mt-4 space-y-2 text-white/90">
            <li><Link to="/about">About Patricia</Link></li>
            <li><Link to="/services">Work With LAYOR</Link></li>
            <li><Link to="/services#advise">Leadership Strategy &amp; Executive Advisory</Link></li>
            <li><Link to="/services#equip">Leadership Training, Speaking &amp; Facilitation</Link></li>
            <li><Link to="/services#build">Leadership Tools &amp; Digital Solutions</Link></li>
            <li><Link to="/services#studio">Digital Studio</Link></li>
            <li><Link to="/what-you-will-build">What We Build</Link></li>
            <li><Link to="/insights">Insights</Link></li>
          </ul>
        </div>

        <div className="md:col-span-4">
          <div className="eyebrow font-semibold" style={{ color: "#E6C871" }}>Contact</div>
          <ul className="mt-4 space-y-2 text-white/90">
            <li><a href={SITE.phoneHref} data-testid="footer-phone">{SITE.phone}</a></li>
            {SITE.email && <li><a href={`mailto:${SITE.email}`} data-testid="footer-email">{SITE.email}</a></li>}
            <li>{SITE.location}</li>
            {SITE.calendly && <li><a href={SITE.calendly} target="_blank" rel="noreferrer" className="underline decoration-[var(--layor-gold)] underline-offset-4">Schedule via Calendly</a></li>}
          </ul>
          <ul className="mt-6 flex gap-4 text-xs text-white/60">
            <li><Link to="/privacy">Privacy</Link></li>
            <li><Link to="/terms">Terms</Link></li>
            <li><Link to="/accessibility">Accessibility</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="mx-auto max-w-7xl px-6 md:px-10 py-6 text-xs text-white/50 flex flex-col md:flex-row items-start md:items-center justify-between gap-2">
          <div>© {new Date().getFullYear()} LAYOR Digital Group. All rights reserved. Your use of this site is subject to our <Link to="/privacy" className="underline decoration-[var(--layor-gold)] underline-offset-4 hover:text-white">Privacy Policy</Link> and <Link to="/terms" className="underline decoration-[var(--layor-gold)] underline-offset-4 hover:text-white">Terms of Use</Link>.</div>
          <div>Leadership before technology.</div>
        </div>
      </div>
    </footer>
  );
};

export default function Layout({ children }) {
  return (
    <div className="min-h-screen bg-[var(--layor-ivory)] text-[var(--layor-text)]">
      <Navbar />
      <main>{children}</main>
      <Footer />
    </div>
  );
}
